'use client';

import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Button } from '@savvy-kit/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@savvy-kit/components/dialog';
import { cn } from '@savvy-kit/lib/utils';
import { isNil } from 'es-toolkit';
import TrackedButton from 'src/components/tracked/tracked-button';
import TrackedDialog from 'src/components/tracked/tracked-dialog';

import type { ParentElementName } from '@savvy/deprecated-shared/tracking/parent-element-view-context';
import type { TrackedClickableElementName } from '@savvy/deprecated-shared/tracking/tracked-clickable-element-name';

/**
 * Restores the analytics a hand-rolled `TrackedDialog` + `TrackedButton` pair emitted, so a
 * component can be replaced by this hook without silently dropping its events. `elementName`
 * is required because it supplies both the dialog-view event and the parent context the button
 * events are attributed to — button names alone would report an undefined parent.
 */
export type ConfirmationTracking = {
  readonly elementName: ParentElementName;
  readonly cancelElementName?: TrackedClickableElementName;
  readonly confirmationElementName?: TrackedClickableElementName;
};

export type ConfirmationConfig = {
  readonly buttonSize?: 'default' | 'sm';
  readonly cancelLabel?: string;
  readonly confirmationLabel: React.ReactNode;
  readonly confirmationVariant?: 'default' | 'destructive';
  readonly confirmationIcon?: React.ReactNode;
  readonly description?: React.ReactNode;
  readonly onCancel?: () => void;
  readonly onConfirmation: () => void | Promise<void>;
  readonly overlayClassName?: string;
  readonly title: React.ReactNode;
  readonly tracking?: ConfirmationTracking;
};

type ConfirmationContextValue = {
  readonly showConfirmation: (options: ConfirmationConfig) => void;
};

const ConfirmationContext = createContext<ConfirmationContextValue | null>(null);

export function ConfirmationDialogProvider({ children }: { readonly children: React.ReactNode }) {
  const [config, setConfig] = useState<ConfirmationConfig | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const showConfirmation = useCallback((options: ConfirmationConfig) => {
    setConfig(options);
  }, []);

  const contextValue = useMemo(() => ({ showConfirmation }), [showConfirmation]);

  const handleClose = useCallback(() => {
    if (!isLoading) setConfig(null);
  }, [isLoading]);

  const handleCancel = useCallback(() => {
    config?.onCancel?.();
    setConfig(null);
  }, [config]);

  const handleConfirm = useCallback(async () => {
    if (isNil(config)) return;

    const result = config.onConfirmation();
    if (!(result instanceof Promise)) {
      setConfig(null);
      return;
    }

    setIsLoading(true);
    try {
      await result;
      setConfig(null);
    } catch {
      // Keep the dialog open so the user can retry; onConfirmation surfaces its own error feedback.
    } finally {
      setIsLoading(false);
    }
  }, [config]);

  const body = isNil(config) ? null : (
    <>
      {/* min-w-0 lets this grid child shrink below its content's min-width so a long
          unbreakable token (e.g. an email in the description) wraps instead of overflowing. */}
      <DialogHeader className="min-w-0">
        <DialogTitle>{config.title}</DialogTitle>
        {/* Rendered only when there is copy: Base UI points `aria-describedby` at this node,
            so an empty one gives the dialog a blank accessible description. */}
        {!isNil(config.description) && (
          <DialogDescription className="break-words">{config.description}</DialogDescription>
        )}
      </DialogHeader>
      <DialogFooter>
        <ConfirmationButton
          elementName={config.tracking?.cancelElementName}
          disabled={isLoading}
          onClick={handleCancel}
          variant="secondary"
          size={config.buttonSize ?? 'sm'}
        >
          {config.cancelLabel ?? 'Cancel'}
        </ConfirmationButton>
        <ConfirmationButton
          elementName={config.tracking?.confirmationElementName}
          disabled={isLoading}
          onClick={handleConfirm}
          variant={config.confirmationVariant ?? 'default'}
          size={config.buttonSize ?? 'sm'}
          isLoading={isLoading}
          icon={config.confirmationIcon}
        >
          {config.confirmationLabel}
        </ConfirmationButton>
      </DialogFooter>
    </>
  );

  // Sit above MUI modals (z-index 1300) so confirmations triggered from
  // within a MUI Dialog (e.g. the note dialog) aren't hidden behind it.
  const panelProps = {
    className: 'z-[1400]',
    overlayClassName: cn('z-[1400]', config?.overlayClassName),
    showCloseButton: !isLoading,
    size: 'sm' as const,
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) handleClose();
  };

  return (
    <ConfirmationContext.Provider value={contextValue}>
      {children}
      {!isNil(config) &&
        // TrackedDialog only when a caller opted into tracking — it emits the view event and
        // provides the parent context TrackedButton reads. Otherwise the plain panel, which is
        // what every pre-existing caller gets.
        (isNil(config.tracking) ? (
          <Dialog onOpenChange={handleOpenChange} open>
            <DialogContent {...panelProps}>{body}</DialogContent>
          </Dialog>
        ) : (
          <TrackedDialog elementName={config.tracking.elementName} onOpenChange={handleOpenChange} open {...panelProps}>
            {body}
          </TrackedDialog>
        ))}
    </ConfirmationContext.Provider>
  );
}

function ConfirmationButton({
  elementName,
  ...props
}: { readonly elementName?: TrackedClickableElementName } & React.ComponentProps<typeof Button>) {
  if (isNil(elementName)) return <Button {...props} />;
  return <TrackedButton elementName={elementName} {...props} />;
}

export function useConfirmationDialog() {
  const context = useContext(ConfirmationContext);
  if (isNil(context)) {
    throw new Error('useConfirmationDialog must be used within ConfirmationDialogProvider');
  }
  return context.showConfirmation;
}
